import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useId } from '../../../../hooks/useId';
import { useMeasureElement } from '../../../../hooks/useMeasureElement';
import isNil from '../../../../utils/isNil';
import { pickAttrs } from '../../../../utils/pickAttrs';
import { FLOATING_DEFAULT_TRANSITION_DURATION } from '../../../Floating/Floating.constants';
import {
    SELECT_DEFAULT_LIST_HEIGHT,
    SELECT_DEFAULT_OPTION_FILTER_PROP,
    SELECT_DEFAULT_OPTION_KEY_PROP,
    SELECT_DEFAULT_OPTION_LABEL_PROP
} from '../../Select.constants';
import { isStringOrNumber, matchText } from '../../Select.helpers';

import { useSelectOptions } from './useSelectOptions';
import { useSelectSelection } from './useSelectSelection';

import type { SelectRef, SelectRootProps } from '../../Select.types';

export const useSelectRoot = (props: SelectRootProps & { selectRef?: React.Ref<SelectRef> }) => {
    const {
        // Basic Properties
        id: idProp,
        size = 'default',
        mode = 'single',
        disabled = false,
        readOnly = false,
        autoFocus = false,
        tabIndex,

        // UI
        loading = false,

        // Values & Selection
        value: controlledValue,
        defaultValue,
        onChange,
        onSelect,
        onDeselect,

        // Options Configuration
        options: optionsProp,
        optionLabelProp = SELECT_DEFAULT_OPTION_LABEL_PROP,
        optionFilterProp = SELECT_DEFAULT_OPTION_FILTER_PROP,
        optionKeyProp = SELECT_DEFAULT_OPTION_KEY_PROP,
        withSelectAll,
        checkmarkAlign,
        hideCheckmark,
        rowComponent,
        disableAutoActiveFirstOption = false,
        animationDuration = FLOATING_DEFAULT_TRANSITION_DURATION,
        destroyOnHide = false,

        // Search & Filter
        showSearch = false,
        searchValue,
        onSearch,
        filterOption,

        // AutoComplete
        autoComplete: autoCompleteProp = false,
        autoCompleteOpen,

        // Dropdown Configuration
        open: openProp,
        listHeight = SELECT_DEFAULT_LIST_HEIGHT,
        dropdownMatchSelectWidth,
        onPopupScroll,

        // Refs
        selectRef: consumerSelectRef,

        ...rest
    } = props;

    // ===== COMPUTED VALUES =====
    const autoComplete = mode === 'single' && autoCompleteProp;
    const isInlineInputSearchVisible = Boolean(mode === 'single' && (showSearch || autoComplete));

    // FIXME: https://sherpany.atlassian.net/browse/SHRP-36582
    // const canUseVirtual = hasDOMMeasurements();
    // const isVirtualScrollEnabled =
    //     canUseVirtual && dropdownMatchSelectWidth !== false && !disableVirtualScroll;
    const isVirtualScrollEnabled = false;
    // -- END FIXME --

    const defaultAutoCompleteOpen = Boolean(optionsProp?.length > 0);
    const isAutoCompleteControlled = Boolean((onChange || onSearch) && autoComplete);
    const id = useId(idProp);
    const ariaProps = pickAttrs(rest, {
        aria: true
    }) as Record<string, unknown>;
    const isInputEditable = !disabled && !readOnly && isInlineInputSearchVisible;
    const autoCompleteInitialValue =
        autoComplete && isStringOrNumber(controlledValue) ? String(controlledValue) : null;

    // ===== STATE MANAGEMENT =====
    const [maxHeight, setMaxHeight] = useState(listHeight);
    const [open, setOpen] = useState(openProp ?? false);
    const [pointer, setPointer] = useState(false);
    const [focused, setFocused] = useState(false);
    const [isSearchFieldFocused, setIsSearchFieldFocused] = useState(false);
    const [isDropdownSearchFieldFocused, setIsDropdownSearchFieldFocused] = useState(false);
    const [internalSearchValue, setInternalSearchValue] = useState(
        (searchValue || autoCompleteInitialValue) ?? ''
    );
    const [rawValue, setValue] = useState(defaultValue ?? controlledValue);
    const [isFloatingDropdownPositioned, setIsFloatingDropdownPositioned] = useState(false);

    // ===== REFS =====
    const virtualScrollContainer = useRef(null);
    const isTypingRef = useRef(false);

    // ===== DERIVED STATE =====
    const [searchContainerRef, { height: searchContainerHeight }] =
        useMeasureElement<HTMLDivElement>({
            keepValuesWhenHidden: !destroyOnHide
        });
    const canDropdownOpen = Boolean(
        !disabled && !readOnly && (!autoComplete || (autoCompleteOpen ?? defaultAutoCompleteOpen))
    );
    const isTypeaheadEnabled = !showSearch && !autoComplete && !isDropdownSearchFieldFocused;

    const value = useMemo(() => {
        if (mode === 'multiple' && !Array.isArray(rawValue)) {
            if (isNil(rawValue)) {
                return [];
            }
            return [rawValue];
        }
        return rawValue;
    }, [rawValue, mode, controlledValue, defaultValue]);

    // ===== VALIDATION =====
    if (typeof value === 'object' && !isNil(value) && !Array.isArray(value)) {
        throw new Error(`Select: cannot be an object, received ${typeof value}`);
    }

    // ===== HELPER FUNCTIONS & EFFECTS =====

    useEffect(() => {
        if (!open && pointer) {
            setPointer(false);
        }
    }, [open, pointer]);

    useEffect(() => {
        if (controlledValue !== undefined) {
            setValue(controlledValue);
        }
    }, [controlledValue]);

    useEffect(() => {
        if (searchValue !== undefined) {
            setInternalSearchValue(searchValue);
        }
    }, [searchValue]);

    const options = useSelectOptions({
        options: optionsProp ?? [],
        disableAutoActiveFirstOption,
        withSelectAll,
        filterOption,
        optionFilterProp,
        optionKeyProp,
        rowComponent,
        mode,
        internalSearchValue,
        autoComplete,
        isVirtualScrollEnabled,
        value,
        virtualScrollContainer
    });
    const selection = useSelectSelection({
        options,
        mode,
        onChange,
        onSelect,
        onDeselect,
        optionKeyProp,
        value,
        setOpen,
        setInternalSearchValue
    });

    const handleSearch = useCallback(
        (query: string) => {
            options.setActiveIndex?.(null);
            setInternalSearchValue(query);
            onSearch?.(query);
            if (query) {
                setOpen(true);
            }

            if (autoComplete) {
                onChange?.(query, {});
            } else {
                const upperSearch = query.toUpperCase();
                // Check if query matches any option and set active index
                const matchedIndex = options.displayOptions.findIndex(
                    (opt) =>
                        matchText(String(opt[optionFilterProp]), upperSearch) ||
                        matchText(String(opt.value), upperSearch) ||
                        matchText(String(opt.name), upperSearch)
                );

                if (matchedIndex !== -1) {
                    options.setActiveIndex?.(matchedIndex);
                } else {
                    options.setActiveIndex?.(null);
                }
            }
        },
        [
            onSearch,
            options.setActiveIndex,
            options.displayOptions,
            autoComplete,
            onChange,
            setInternalSearchValue,
            setOpen
        ]
    );

    const contextValue = useMemo(
        () => ({
            // Basic Properties
            id,
            disabled,
            readOnly,
            size,
            mode,
            tabIndex,
            autoFocus,
            loading,

            // AutoComplete & Search
            autoComplete,
            showSearch,
            isAutoCompleteControlled,
            isInputEditable,
            isTypeaheadEnabled,
            internalSearchValue,
            setInternalSearchValue,
            handleSearch,
            isSearchFieldFocused,
            setIsSearchFieldFocused,
            setIsDropdownSearchFieldFocused,
            controlledValue,
            searchValue,

            // UI State & Layout
            isInlineInputSearchVisible,
            isVirtualScrollEnabled,
            searchContainerHeight,
            maxHeight,
            setMaxHeight,
            listHeight,

            // Values & Selection
            value,
            setValue,
            options,
            selection,

            // Dropdown State
            open,
            canDropdownOpen,
            setOpen,
            destroyOnHide,
            animationDuration,
            pointer,
            setPointer,
            focused,
            setFocused,
            dropdownMatchSelectWidth,
            isFloatingDropdownPositioned,
            setIsFloatingDropdownPositioned,

            // Option Configuration
            optionLabelProp,
            optionKeyProp,
            optionFilterProp,
            checkmarkAlign,
            hideCheckmark,
            rowComponent,

            // Event Handlers & Callbacks
            onPopupScroll,

            // Refs & Virtual Scrolling
            virtualScrollContainer,
            isTypingRef,
            consumerSelectRef,
            searchContainerRef,

            // Accessibility
            ariaProps
        }),
        [
            // Basic Properties
            id,
            disabled,
            readOnly,
            size,
            mode,
            tabIndex,
            autoFocus,
            loading,

            // AutoComplete & Search
            autoComplete,
            showSearch,
            isAutoCompleteControlled,
            isInputEditable,
            isTypeaheadEnabled,
            internalSearchValue,
            handleSearch,
            isSearchFieldFocused,
            controlledValue,
            searchValue,

            // UI State & Layout
            isInlineInputSearchVisible,
            isVirtualScrollEnabled,
            searchContainerHeight,
            maxHeight,
            listHeight,

            // Values & Selection
            value,
            options,
            selection,

            // Dropdown State
            open,
            canDropdownOpen,
            destroyOnHide,
            animationDuration,
            pointer,
            focused,
            dropdownMatchSelectWidth,
            isFloatingDropdownPositioned,

            // Option Configuration
            optionLabelProp,
            optionKeyProp,
            optionFilterProp,
            checkmarkAlign,
            hideCheckmark,
            rowComponent,

            // Event Handlers & Callbacks
            onPopupScroll,

            // Refs
            consumerSelectRef,

            // Accessibility
            JSON.stringify(ariaProps)
        ]
    );

    return contextValue;
};

export type SelectRootValues = ReturnType<typeof useSelectRoot>;
